import AddressCellRenderer from '@gu-datagrid-new/cell-renderers/AddressCellRenderer.vue'
import BadgeCellRenderer from '@gu-datagrid-new/cell-renderers/BadgeCellRenderer.vue'
import PhoneCellRenderer from '@gu-datagrid-new/cell-renderers/PhoneCellRenderer.vue'

import PgeCompaniesActionsCellRenderer from '@customers/pge/components/cell-renderers/PgeCompaniesActionsCellRenderer.vue'
import PgeCompaniesAttributesCellRenderer from '@customers/pge/components/cell-renderers/PgeCompaniesAttributesCellRenderer.vue'
import PgeCompaniesLinkCellRenderer from '@customers/pge/components/cell-renderers/PgeCompaniesLinkCellRenderer.vue'

const defaultColDef = {
  menuTabs: [
    'filterMenuTab',
    'generalMenuTab',
    'columnsMenuTab',
  ],
  resizable: true,
  sortable: true,
  filter: true,
  // Brief yellow flash when a cell's value changes (e.g. after a rule action
  // returns UPDATE_FIELD_VALUE). Matches Vue2 PG&E grid behavior.
  enableCellChangeFlash: true,
}

// Mirrors qadocontractors.newcollab/companymanagement/frontend/src/config/companies.pge.columns.json
// (the Vue2 PG&E columns). Differences from default ILCM columns:
//  - CSLB column (PG&E construction license number)
//  - Attributes column (WDT/Non-Export badges)
//  - No Roles column
//  - Status badge variants match Vue2 StatusCellRenderer:
//    Active=info, Approved=success, Pending=secondary, Disabled=danger
const baseColumnDefs = [
  {
    field: 'rowId',
    hide: true,
  },
  {
    headerName: 'ID',
    field: 'ID',
    type: 'numericColumn',
    cellClass: 'number-cell text-muted',
    pinned: 'left',
    width: 60,
    minWidth: 60,
    filter: 'agNumberColumnFilter',
  },
  {
    headerName: 'Company',
    field: 'Company',
    cellRenderer: PgeCompaniesLinkCellRenderer,
    filter: 'agTextColumnFilter',
    filterParams: { suppressAndOrCondition: true },
    minWidth: 220,
  },
  {
    headerName: 'CSLB',
    field: 'CSLB',
    filter: 'agTextColumnFilter',
    filterParams: { suppressAndOrCondition: true },
    width: 100,
    minWidth: 100,
  },
  {
    headerName: 'Email',
    field: 'Email',
    filter: 'agTextColumnFilter',
    filterParams: { suppressAndOrCondition: true },
    minWidth: 200,
  },
  {
    headerName: 'Address',
    valueGetter: (params) =>
      [params.data?.Address, params.data?.City, params.data?.State, params.data?.Zip]
        .filter(Boolean)
        .join(' '),
    cellRenderer: AddressCellRenderer,
    cellRendererParams: {
      streetAddress: 'Address',
      city: 'City',
      state: 'State',
      zip: 'Zip',
    },
    sortable: false,
    filter: false,
    minWidth: 150,
    cellClass: 'multiline',
  },
  {
    headerName: 'Phone',
    field: 'Phone',
    cellRenderer: PhoneCellRenderer,
    cellRendererParams: {
      phone: 'Phone',
      type: 'Phone Type',
    },
    sortable: false,
    filter: false,
    minWidth: 120,
    cellClass: 'multiline',
  },
  {
    headerName: 'Attributes',
    field: 'Attributes',
    cellRenderer: PgeCompaniesAttributesCellRenderer,
    valueFormatter: (params) => Array.isArray(params.value) ? params.value.join(', ') : '',
    sortable: false,
    filter: false,
    minWidth: 100,
  },
  {
    headerName: 'Status',
    field: 'Status',
    cellRenderer: BadgeCellRenderer,
    cellRendererParams: {
      variant: {
        Active: 'info',
        Approved: 'success',
        Pending: 'secondary',
        Disabled: 'danger',
      },
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressAndOrCondition: true,
      values: ['Active', 'Approved', 'Pending', 'Disabled'],
    },
    minWidth: 100,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    cellRenderer: PgeCompaniesActionsCellRenderer,
    valueFormatter: (params) =>
      Array.isArray(params.value) ? params.value.map((a) => a.label || a.Label).join(', ') : '',
    sortable: false,
    filter: false,
    minWidth: 200,
    pinned: 'right',
    suppressHeaderMenuButton: true,
  },
]

export function buildPgeCompaniesGridConfig ({ rowData, context } = {}) {
  return {
    defaultColDef,
    columnDefs: baseColumnDefs,
    rowData: rowData ?? [],
    pagination: true,
    // Vue2 PG&E parity: Export CSV/Excel button in the toolbar.
    enableExportButton: true,
    exportButtonSettings: {
      defaultFilename: 'Companies Export',
      header: 'Export Companies',
    },
    enableMultirowSelect: false,
    enablePresets: false,
    enableQuickFilter: true,
    showActiveFilters: true,
    gridOptions: {
      // Vue2 PG&E parity: row count summary in the bottom status bar.
      // `agTotalAndFilteredRowCountComponent` shows current total respecting filters,
      // `agTotalRowCountComponent` shows the unfiltered count, and
      // `agFilteredRowCountComponent` shows the filtered subset count.
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agTotalRowCountComponent', align: 'center' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'center' },
        ],
      },
      // Vue2 PG&E parity: page size auto-fits to available container height
      // (Vue2 default-grid-options.js sets `paginationAutoPageSize: true`).
      // Overrides GuDatagridClient's hardcoded `paginationPageSize: 20` per
      // AG-Grid precedence rules.
      paginationAutoPageSize: true,
      defaultColumnId: 'ID',
      context: context ?? {},
      overlayNoRowsTemplate: '<div class="alert alert-info">No companies in system</div>',
    },
  }
}
